import { useEffect, useRef, useState } from "react";
import { Instagram, ExternalLink } from "lucide-react";
import useReveal from "../../hooks/use-reveal";

const FEED_ID = process.env.REACT_APP_BEHOLD_FEED_ID;
const IG_HANDLE = "drvenkatsubramaniam";

export const InstagramSection = () => {
  useReveal();
  const ref = useRef(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  useEffect(() => {
    if (!FEED_ID) return;
    // Load the Behold widget script once
    const existing = document.querySelector(
      'script[src="https://w.behold.so/widget.js"]'
    );
    if (existing) {
      setScriptLoaded(true);
      return;
    }
    const s = document.createElement("script");
    s.type = "module";
    s.src = "https://w.behold.so/widget.js";
    s.async = true;
    s.onload = () => setScriptLoaded(true);
    document.head.appendChild(s);
  }, []);

  return (
    <section
      id="instagram"
      className="py-20 sm:py-28 px-6 sm:px-10 lg:px-14 bg-bone-2"
    >
      <div className="max-w-7xl mx-auto">
        <div className="reveal grid lg:grid-cols-12 gap-8 mb-12 items-end">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-3">Latest from Instagram</p>
            <h2 className="font-sans font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink leading-tight">
              Patient education, straight from the OR.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-ink-soft max-w-2xl">
              Surgical insights, recovery tips and patient stories — Dr. Venkat shares them on Instagram. Follow along for updates that demystify urological care.
            </p>
          </div>
          <div className="lg:col-span-5 flex lg:justify-end">
            <a
              href={`https://www.instagram.com/${IG_HANDLE}/`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-follow-btn"
              className="inline-flex items-center gap-2 bg-paper border border-line hover:border-forest hover:text-forest text-ink px-6 py-3 rounded-full text-sm font-semibold transition"
            >
              <Instagram size={16} />
              @{IG_HANDLE}
              <ExternalLink size={14} />
            </a>
          </div>
        </div>

        <div className="reveal" data-testid="instagram-feed">
          {FEED_ID ? (
            <div
              ref={ref}
              className="rounded-3xl overflow-hidden bg-paper border border-line p-3 sm:p-5"
            >
              {/* Behold web component — renders live Instagram feed */}
              <behold-widget feed-id={FEED_ID}></behold-widget>
              {!scriptLoaded && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      className="aspect-square rounded-2xl bg-bone-2 animate-pulse"
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <FeedPlaceholder />
          )}
        </div>
      </div>
    </section>
  );
};

const FeedPlaceholder = () => (
  <div className="rounded-3xl border border-dashed border-line bg-paper p-10 text-center">
    <Instagram size={36} className="mx-auto text-ink-soft mb-4" strokeWidth={1.5} />
    <p className="font-sans font-semibold text-ink">Instagram feed loading soon</p>
    <p className="mt-2 text-sm text-ink-soft max-w-md mx-auto">
      Once the Behold feed is connected, the latest Instagram posts and reels from{" "}
      <a
        href={`https://www.instagram.com/${IG_HANDLE}/`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-forest font-semibold link-underline"
      >
        @{IG_HANDLE}
      </a>{" "}
      will appear here automatically.
    </p>
  </div>
);
